import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Sun, Moon, Bell, BellOff, User, Mail, Building, Briefcase } from 'lucide-react';

export default function SettingsPage() {
  const { user, employee } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { desktopPermission, notificationsEnabled, toggleNotifications, requestDesktopPermission } = useNotifications();

  const handleNotificationToggle = async (checked) => {
    const success = await toggleNotifications(checked);
    if (checked && success) {
      toast.success('Desktop notifications enabled');
    } else if (checked && !success) {
      toast.error('Please allow notifications in your browser settings');
    } else if (!checked) {
      toast.success('Desktop notifications disabled');
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account and preferences
          </p>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
            <CardDescription>
              Your account information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              <div>
                <p className="font-semibold text-lg">{user?.name}</p>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            
            <Separator />
            
            {employee && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Role:</span>
                  <span className="font-medium">{employee.role}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Department:</span>
                  <span className="font-medium">{employee.department}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Employee Code:</span>
                  <span className="font-medium">{employee.employee_code}</span>
                </div>
                {employee.line_manager_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Line Manager:</span>
                    <span className="font-medium">{employee.line_manager_name}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {theme === 'light' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              Appearance
            </CardTitle>
            <CardDescription>
              Customize the look and feel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Dark Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Switch between light and dark theme
                </p>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications
            </CardTitle>
            <CardDescription>
              Configure notification preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Desktop Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive browser notifications for updates
                </p>
              </div>
              <Switch
                checked={notificationsEnabled && desktopPermission === 'granted'}
                onCheckedChange={handleNotificationToggle}
                disabled={desktopPermission === 'denied'}
                data-testid="notifications-toggle"
              />
            </div>
            
            {desktopPermission === 'denied' && (
              <div className="p-3 bg-destructive/10 rounded-lg flex items-start gap-2">
                <BellOff className="w-4 h-4 text-destructive mt-0.5" />
                <p className="text-sm text-destructive">
                  Notifications are blocked. Please enable them in your browser settings.
                </p>
              </div>
            )}
            
            {desktopPermission === 'granted' && notificationsEnabled && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-start gap-2">
                <Bell className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5" />
                <p className="text-sm text-green-700 dark:text-green-300">
                  You will receive desktop notifications for ticket updates, approvals, and assignments.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
